/**
 * careerFeedDb.js
 * Dexie (IndexedDB) database for CareerDashboard ProFeed / IndustryFeed.
 *
 * Replaces localStorage for the three large stores that can exceed 5 MB:
 *   • careerFeedItems   – cached RSS/career articles, keyed by guid
 *   • careerFetchTimes  – per-career last-fetch timestamps
 *   • careerNotes       – per-article user notes, keyed by guid
 *
 * Usage:
 *   import {
 *     loadCareerFeedItems, saveCareerFeedItems,
 *     loadCareerFetchTimes, saveCareerFetchTime,
 *     loadCareerNotes, saveCareerNote,
 *     migrateCareerFeedFromLocalStorage,
 *   } from './careerFeedDb';
 */

import Dexie from 'dexie';

const db = new Dexie('CareerFeedDB');

db.version(1).stores({
  /**
   * careerFeedItems
   * Primary key : [userId+guid]  (compound – isolates users on shared devices)
   * Indexed     : userId, category_name  (fast per-user / per-career queries)
   */
  careerFeedItems: '[userId+guid], userId, category_name, pub_date',

  /**
   * careerFetchTimes
   * Primary key : [userId+career]
   */
  careerFetchTimes: '[userId+career], userId',

  /**
   * careerNotes
   * Primary key : [userId+guid]
   */
  careerNotes: '[userId+guid], userId',
});

// ─────────────────────────────────────────────────────────────────────────────
//  careerFeedItems helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all career feed items for a user as a { guid: item } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCareerFeedItems(userId) {
  const rows = await db.careerFeedItems.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row;
  });
  return map;
}

/**
 * Upsert (put) an array of career articles for a user.
 * Each article must have a `guid`.
 * @param {string} userId
 * @param {Object[]} articles
 */
export async function saveCareerFeedItems(userId, articles) {
  const rows = articles.map((a) => ({ ...a, userId }));
  await db.careerFeedItems.bulkPut(rows);
}

// ─────────────────────────────────────────────────────────────────────────────
//  careerFetchTimes helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all fetch timestamps for a user as { career: timestamp } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCareerFetchTimes(userId) {
  const rows = await db.careerFetchTimes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.career] = row.timestamp;
  });
  return map;
}

/**
 * Upsert the fetch timestamp for a single career.
 * @param {string} userId
 * @param {string} career
 * @param {number} timestamp  – Date.now()
 */
export async function saveCareerFetchTime(userId, career, timestamp) {
  await db.careerFetchTimes.put({ userId, career, timestamp });
}

// ─────────────────────────────────────────────────────────────────────────────
//  careerNotes helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all notes for a user as { guid: noteText } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCareerNotes(userId) {
  const rows = await db.careerNotes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row.text;
  });
  return map;
}

/**
 * Upsert a single career note.
 * @param {string} userId
 * @param {string} guid
 * @param {string} text
 */
export async function saveCareerNote(userId, guid, text) {
  await db.careerNotes.put({ userId, guid, text });
}

/**
 * Load all career notes for a user as raw rows (not the collapsed guid→text
 * map that loadCareerNotes returns) — used by the export engine, which needs
 * the row shape intact rather than pre-flattened.
 * @param {string} userId
 * @returns {Promise<Array<{userId: string, guid: string, text: string}>>}
 */
export async function getAllCareerNotesForExport(userId) {
  return await db.careerNotes.where('userId').equals(userId).toArray();
}

// ─────────────────────────────────────────────────────────────────────────────
//  One-time migration: localStorage → IndexedDB
//  Call once on mount; self-removes old keys when done.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Migrates legacy careerfeed localStorage data for this user into IndexedDB,
 * then removes the old keys to free quota.
 * Safe to call on every mount — no-op if the keys are already absent.
 * @param {string} userId
 */
export async function migrateCareerFeedFromLocalStorage(userId) {
  // ── feed items ──────────────────────────────────────────────────────────
  const rawItems = localStorage.getItem(`careerfeed_all_items_${userId}`);
  if (rawItems) {
    try {
      const itemMap = JSON.parse(rawItems); // { guid: article }
      const articles = Object.values(itemMap);
      if (articles.length > 0) {
        await saveCareerFeedItems(userId, articles);
      }
      localStorage.removeItem(`careerfeed_all_items_${userId}`);
      console.log(`[CareerFeedDB] Migrated ${articles.length} feed items for user ${userId}`);
    } catch (e) {
      console.warn('[CareerFeedDB] Could not migrate feed items:', e);
    }
  }

  // ── fetch timestamps ────────────────────────────────────────────────────
  const rawTimes = localStorage.getItem(`careerfeed_fetch_times_${userId}`);
  if (rawTimes) {
    try {
      const timesMap = JSON.parse(rawTimes); // { career: timestamp }
      for (const [career, timestamp] of Object.entries(timesMap)) {
        await saveCareerFetchTime(userId, career, timestamp);
      }
      localStorage.removeItem(`careerfeed_fetch_times_${userId}`);
      console.log('[CareerFeedDB] Migrated fetch timestamps for user', userId);
    } catch (e) {
      console.warn('[CareerFeedDB] Could not migrate fetch times:', e);
    }
  }

  // ── notes ───────────────────────────────────────────────────────────────
  const rawNotes = localStorage.getItem(`careerfeed_notes_${userId}`);
  if (rawNotes) {
    try {
      const notesMap = JSON.parse(rawNotes); // { guid: text }
      for (const [guid, text] of Object.entries(notesMap)) {
        await saveCareerNote(userId, guid, text);
      }
      localStorage.removeItem(`careerfeed_notes_${userId}`);
      console.log('[CareerFeedDB] Migrated notes for user', userId);
    } catch (e) {
      console.warn('[CareerFeedDB] Could not migrate notes:', e);
    }
  }
}

/**
 * Bulk-restore career notes from a backup (raw rows, same shape as
 * getAllCareerNotesForExport returns). Upserts by [userId+guid].
 * @param {Array<{userId: string, guid: string, text: string}>} rows
 */
export async function bulkPutCareerNotes(rows) {
  if (!rows || rows.length === 0) return;
  await db.careerNotes.bulkPut(rows);
}

export default db;
export { db as careerFeedDb };